import React, { useEffect, useState, useCallback } from 'react';

// Department × Feature access matrix (ACCESS-CONTROL-AND-ISOLATION-PLAN.md Track A).
// An org admin grants each department access to specific gateable modules.
// Semantics mirror backend module_gating.module_allowed:
//   - org modules_enabled is the entitlement ceiling (a module disabled for the
//     org is not grantable — its column is disabled).
//   - a department that is "Restricted" is narrowed to its checked modules;
//     an unrestricted department is ABSENT from department_grants and inherits
//     org-wide access ("no entry => inherit").
// Server-side middleware is the authoritative enforcer; this UI just edits the
// department_grants stored on the org's module config.

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Gateable modules = backend MODULE_ROUTE_PREFIXES keys, with display labels.
const GATEABLE_MODULES = [
  { key: 'inventory', label: 'Inventory' },
  { key: 'events', label: 'Events' },
  { key: 'scheduling', label: 'Scheduling' },
  { key: 'directconnect', label: 'Direct Connect' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'implementations', label: 'Implementations' },
  { key: 'calculator', label: 'Cost Analysis' },
];

export default function AccessMatrix() {
  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id') || '';

  const [cfg, setCfg] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [grants, setGrants] = useState({});        // { dept: [module_key, ...] }
  const [restricted, setRestricted] = useState({}); // { dept: bool }
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!orgId) { setError('No active organization.'); setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const [cfgRes, deptRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/module-config?organization_id=${encodeURIComponent(orgId)}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/platform/departments?organization_id=${encodeURIComponent(orgId)}`, { headers: authHeaders }),
      ]);
      const cfgJson = cfgRes.ok ? await cfgRes.json() : null;
      const deptJson = deptRes.ok ? await deptRes.json() : null;

      setCfg(cfgJson);
      const dg = (cfgJson && cfgJson.department_grants) || {};
      setGrants(dg);
      const r = {};
      Object.keys(dg).forEach((d) => { r[d] = true; });
      setRestricted(r);
      setDepartments((deptJson && (deptJson.merged || deptJson.fixed)) || []);
    } catch (e) {
      setError('Failed to load access configuration.');
    } finally {
      setLoading(false);
    }
  }, [orgId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const orgAllows = (moduleKey) => (cfg?.modules_enabled?.[moduleKey] !== false);

  const isChecked = (dept, moduleKey) => (grants[dept] || []).includes(moduleKey);

  const toggleModule = (dept, moduleKey) => {
    if (!restricted[dept] || !orgAllows(moduleKey)) return;
    setGrants((prev) => {
      const current = new Set(prev[dept] || []);
      current.has(moduleKey) ? current.delete(moduleKey) : current.add(moduleKey);
      return { ...prev, [dept]: Array.from(current) };
    });
    setStatus('');
  };

  const toggleRestricted = (dept) => {
    setRestricted((prev) => {
      const next = { ...prev, [dept]: !prev[dept] };
      // Turning restriction ON with no prior selection defaults to the modules
      // the org currently allows, so the admin starts from "all granted" and
      // removes, rather than an empty (fully-denied) row by surprise.
      if (next[dept] && !(grants[dept] && grants[dept].length)) {
        setGrants((g) => ({ ...g, [dept]: GATEABLE_MODULES.filter((m) => orgAllows(m.key)).map((m) => m.key) }));
      }
      return next;
    });
    setStatus('');
  };

  const save = async () => {
    if (!cfg) return;
    setStatus('Saving…'); setError('');
    // Only "Restricted" departments become entries in department_grants;
    // unrestricted departments are omitted so they inherit org-wide access.
    const department_grants = {};
    Object.keys(restricted).forEach((dept) => {
      if (restricted[dept]) department_grants[dept] = grants[dept] || [];
    });
    try {
      const res = await fetch(`${API_URL}/api/platform/module-config`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cfg, department_grants }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const stored = await res.json();
      setCfg(stored);
      setStatus('Saved.');
    } catch (e) {
      setStatus('');
      setError('Save failed. You need org owner/admin permission.');
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted">Loading access configuration…</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="access-matrix">
      <h1 className="text-xl font-semibold mb-1">Access &amp; Permissions</h1>
      <p className="text-sm text-muted mb-4">
        Grant each department access to specific features. Unrestricted departments see every feature
        your plan includes. Restricting a department limits it to the checked features only.
      </p>

      {error && <div className="mb-3 text-sm text-danger" role="alert">{error}</div>}

      <div className="overflow-x-auto border border-surface-border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface border-b border-surface-border">
              <th className="text-left p-3 font-medium">Department</th>
              <th className="text-center p-3 font-medium">Restrict</th>
              {GATEABLE_MODULES.map((m) => (
                <th key={m.key} className={`text-center p-3 font-medium ${orgAllows(m.key) ? '' : 'opacity-40'}`}>
                  {m.label}
                  {!orgAllows(m.key) && <div className="text-[10px] text-muted">off (plan)</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {departments.map((dept) => (
              <tr key={dept} className="border-b border-surface-border last:border-0">
                <td className="p-3 capitalize">{dept}</td>
                <td className="text-center p-3">
                  <input
                    type="checkbox"
                    checked={!!restricted[dept]}
                    onChange={() => toggleRestricted(dept)}
                    data-testid={`restrict-${dept}`}
                    aria-label={`Restrict ${dept}`}
                  />
                </td>
                {GATEABLE_MODULES.map((m) => (
                  <td key={m.key} className="text-center p-3">
                    {restricted[dept] ? (
                      <input
                        type="checkbox"
                        disabled={!orgAllows(m.key)}
                        checked={isChecked(dept, m.key) && orgAllows(m.key)}
                        onChange={() => toggleModule(dept, m.key)}
                        data-testid={`grant-${dept}-${m.key}`}
                        aria-label={`Grant ${m.label} to ${dept}`}
                      />
                    ) : (
                      <span className="text-muted" title="Inherits org-wide access">✓</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={save}
          className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:opacity-90"
          data-testid="access-save"
        >
          Save changes
        </button>
        {status && <span className="text-sm text-muted" data-testid="access-status">{status}</span>}
      </div>
    </div>
  );
}
